// ─── Talent Mobility (Employee) guided tour ───────────────────────────────────
export const TOUR_MODULE_KEY = "talent-mobility-employee";

// Cross-page tour phases (namespaced). The pathways view opens one position's
// opportunity page for the Skill Gap step, then resumes at Apply.
export const CROSS_PAGE_TOUR_PHASE = {
  SKILL_GAP: "talent-mobility-employee:skill-gap", // show the Skill Gap step there
  APPLY: "talent-mobility-employee:apply", // back on the pathways view, resume at Apply
  FINISH: "talent-mobility-employee:finish", // the Skill Gap step was dismissed, resume at Finish
};

// The page has two first screens, so the tour branches on `state`:
//   "always"      — every branch
//   "form"        — the career-aspiration form (no target saved yet, or Edit)
//   "aspiration"  — the pathways view, whatever it holds
//   "pathways"    — the pathways view with at least one pathway showing
//   "positions"   — …and at least one open position on a pathway
//   "noPathways"  — the pathways view with nothing to show
// `page` steps are shown by the opportunity page.
export const TOUR_STEPS = [
  {
    // Welcome — centered greeting
    state: "always",
    title: "Welcome to Talent Mobility",
    description:
      "Tell SkillsBridge where you want your career to go and it shows you the real paths there, and the open roles along the way. Two minutes and you're set.",
    primaryText: "Start the Tour",
    secondaryText: "I'll explore on my own",
  },
  {
    state: "form",
    key: "careerTarget",
    title: "Set Career Target",
    description:
      "Start by telling us where you're aiming: the legal entity, business unit, function, sub-function and level you're targeting. Each choice narrows the next.",
    placement: "right",
  },
  {
    state: "form",
    key: "targetRole",
    title: "Target Role",
    description:
      "Then pick the role itself. The list is every published job description that fits your target — search it by name.",
    placement: "right",
  },
  {
    state: "form",
    key: "relocation",
    title: "Relocation & Preferred Locations",
    description:
      "Say whether you'd move for the right role. Answer yes and you can pick the locations you'd consider — or tick All Locations.",
    placement: "right",
  },
  {
    state: "form",
    key: "saveContinue",
    title: "Save & Continue",
    description:
      "Save & Continue and SkillsBridge builds your pathways — the routes from where you are to where you're going, and the open roles on each one. Match scores can take a little longer to arrive than the pathways themselves.",
    placement: "top",
  },
  {
    state: "aspiration",
    key: "summaryBar",
    title: "Career Target",
    description:
      "Your target and your location preferences sit here at the top. Click Edit any time to change them — your pathways rebuild around the new target.",
    placement: "bottom",
  },
  {
    state: "aspiration",
    key: "pathwayFilters",
    title: "Pathway Filters",
    description:
      "Filter your pathways: keep the ones inside your own function, the ones that cross into another, or leave both unticked to see everything.",
    placement: "bottom",
  },
  {
    state: "pathways",
    key: "pathwayCard",
    title: "Pathways",
    description:
      "Each card is one pathway. The chain across the top is the full progression — the role you're in now, the steps in between, and your target at the end.",
    placement: "top",
  },
  {
    state: "pathways",
    key: "openPositions",
    title: "Open Positions on This Path",
    description:
      "Under each path are the open positions linked to it, with your match score and how many skills you'd need to build for each.",
    placement: "top",
  },
  {
    state: "positions",
    key: "matchScore",
    title: "Match Score & Skills to Develop",
    description:
      "The percentage is your Skill Match Index for that role — how much of what the role needs you already have. The count beside it is what's still missing.",
    placement: "left",
  },
  {
    state: "positions",
    key: "skillGapDetail",
    page: CROSS_PAGE_TOUR_PHASE.SKILL_GAP,
    title: "Skill Gap Detail",
    description:
      "Open a position to see exactly which skills you'd need to build, and by how much, before you apply.",
    placement: "top",
  },
  {
    state: "positions",
    key: "apply",
    resumePhase: CROSS_PAGE_TOUR_PHASE.APPLY,
    title: "Apply",
    description:
      "Ready? Apply puts you forward for the role — you'll show up on that role's applicant list for the hiring manager and HR.",
    placement: "left",
  },
  {
    state: "noPathways",
    key: "noPathways",
    title: "No Pathways Yet",
    description:
      "No pathways for that target yet. Try widening your target role or the locations you'd consider, and we'll look again.",
    // The pathway filters can empty the list on their own — a different fix.
    filteredDescription:
      "None of your pathways match the filters you've ticked. Untick them to see every pathway again.",
    placement: "top",
  },
  {
    // Finish — centered greeting. Primary brings the pathways (or, on the
    // aspiration form, the Set Career Target card) into focus.
    state: "always",
    resumePhase: CROSS_PAGE_TOUR_PHASE.FINISH,
    title: "You're all set!",
    description: "Your pathways update as new roles open, so it's worth checking back.",
    primaryText: "See My Pathways",
    formPrimaryText: "Set My Career Target",
    secondaryText: "Done",
  },
];

const STEP_STATE_ACTIVE = ({ isAspirationForm, hasPathways, hasPositions }) => ({
  always: true,
  form: isAspirationForm,
  aspiration: !isAspirationForm,
  pathways: !isAspirationForm && hasPathways,
  positions: !isAspirationForm && hasPositions,
  noPathways: !isAspirationForm && !hasPathways,
});

// Every step for the current branch, in spec order — including the opportunity
// page's step, so the pathways view can tell when advancing should hop pages.
export const getTourSteps = (branch) => {
  const active = STEP_STATE_ACTIVE(branch);
  return TOUR_STEPS.filter((step) => active[step.state]);
};

// The step the opportunity page shows on the tour's behalf.
export const SKILL_GAP_TOUR_STEPS = TOUR_STEPS.filter(
  (step) => step.page === CROSS_PAGE_TOUR_PHASE.SKILL_GAP,
);
